import { useRef, useState } from "react";
import styled from "@emotion/styled";
import { Mentions } from "antd5";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import TooltipComponent from "components/ui/tooltip/Tooltip";
import { convertMarkdownToText, extractLinkRegions, getDisplayUserName } from "lib/comment_utils";
import {
  TextHighlighterMatch,
  TextMatchHighlighter,
} from "lib/core_components/TextMatchHighlighter";
import { CurrentUserData } from "lib/hooks/api/users/useCurrentUser";
import { useUsers } from "lib/hooks/api/useUsers";
import { toHumanRelativeTimeFromNow } from "lib/utils/relative_time";
import UserInitials from "./UserInitials";

export type CommentDto = {
  id: string;
  content: string;
  createdAt: string;
  updatedAt?: string | null;
  author?: {
    guid: string;
    firstName: string;
    lastName: string;
    active: boolean;
  } | null;
};

type Props = {
  comment: CommentDto;
  user: CurrentUserData;
  enabledEdit: (id: string) => void;
  handleDelete: (id: string) => void;
  editCommentId: string;
  editedComment: string;
  setEditedComment: (comment: string) => void;
  handleUpdate: (id: string) => void;
  setEditCommentId: (id: string) => void;
};

export function CommentComponent({
  comment,
  user,
  enabledEdit,
  handleDelete: handleDeleteReal,
  editCommentId,
  editedComment,
  setEditedComment,
  handleUpdate,
  setEditCommentId,
}: Props) {
  // includeInactive: true so @mentions of now-deactivated users in old comments still resolve.
  // The autocomplete options below are filtered to active-only.
  const { data: users } = useUsers({ includeInactive: true });
  const mentionsContainerRef = useRef<HTMLDivElement>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const userOptions = users
    ?.filter((user) => user.active !== false)
    .map((user) => ({
      value: getDisplayUserName(user),
      label: getDisplayUserName(user),
    }));

  const parseContentForUserMentions = (content: string) => {
    const possibleMentions = users?.map((u) => {
      return { tag: `@${u.firstName} ${u.lastName}`, guid: u.guid };
    });

    let editableContent = content;

    /**
     * I've done the check this way round, because we can't consistently rely on @"FirstName LastName"
     * as a regex pattern to pull users from the content.
     *
     * We don't validate the user input, so it's possible for the first or last name to be multiple words.
     */
    possibleMentions?.forEach((mention) => {
      const re = new RegExp(mention.tag, "g");
      const match = content?.match(re);
      if (match) {
        editableContent = editableContent.replace(
          mention.tag,
          `@[${mention.tag}](${mention.guid})`,
        );
      }
    });

    let matches: TextHighlighterMatch[] = [];
    if (editableContent) {
      matches = extractLinkRegions(editableContent);
    }

    return <TextMatchHighlighter text={editableContent} matches={matches} newCommentStyle={true} />;
  };

  const handleEditPrompt = () => {
    enabledEdit(comment.id);
    setConfirmDelete(false);
  };

  const handleDeletePrompt = () => {
    setEditCommentId("");
    setConfirmDelete(true);
  };

  const handleDelete = () => {
    handleDeleteReal(comment.id);
    setConfirmDelete(false);
  };

  const authorFirstName = comment.author?.firstName ?? "Unknown";
  const authorLastName = comment.author?.lastName ?? "User";

  return (
    <Flex gap="12px">
      <UserInitials firstName={authorFirstName} lastName={authorLastName} color="grey" />
      <CommentContent column gap="8px" flexGrow={1}>
        <Flex gap="8px" flexWrap="wrap" alignItems="center">
          <Text bold>
            {authorFirstName} {authorLastName}
          </Text>
          {comment.createdAt && (
            <Text ellipsis description color="sysTextSecondary">
              {toHumanRelativeTimeFromNow(comment.createdAt)}
            </Text>
          )}
          <Spacer />
          {comment.updatedAt && comment.updatedAt !== comment.createdAt && (
            <TooltipComponent content={toHumanRelativeTimeFromNow(comment.updatedAt)}>
              <Text description color="sysTextSecondary">
                Edited
              </Text>
            </TooltipComponent>
          )}
          {user.guid === comment.author?.guid && (
            <DropdownMenu
              trigger={
                <DropdownMenuTrigger
                  className={dropdownOpen ? "dropdown-trigger active" : "dropdown-trigger"}
                  icon="dotsHorizontal"
                  hideArrow
                  buttonType="primary"
                  buttonStyle="inverse"
                  aria-label="Comment actions"
                />
              }
              align="end"
              width={160}
              open={dropdownOpen}
              onOpenChange={setDropdownOpen}
            >
              <DropdownMenuItem key="edit" icon="edit02" onClick={handleEditPrompt}>
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                key="delete"
                icon="trash01"
                iconProps={{ color: "sysStatusNegative" }}
                onClick={handleDeletePrompt}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenu>
          )}
        </Flex>
        {editCommentId === comment.id ? (
          <>
            <div ref={mentionsContainerRef}>
              <MentionsStyled
                options={userOptions}
                placeholder="Edit comment"
                value={convertMarkdownToText(editedComment)}
                onChange={(e) => setEditedComment(e)}
                autoSize
                getPopupContainer={() => mentionsContainerRef.current ?? document.body}
              />
            </div>
            <Flex gap="4px" justifyContent="flex-end">
              <Button as="button" buttonType="secondary" onClick={() => setEditCommentId("")}>
                Cancel
              </Button>
              <Button as="button" buttonType="primary" onClick={() => handleUpdate(comment.id)}>
                Update
              </Button>
            </Flex>
          </>
        ) : (
          <>
            <Text>{parseContentForUserMentions(comment.content)}</Text>
            {confirmDelete && (
              <Flex gap="4px" justifyContent="flex-end">
                <Button as="button" buttonType="secondary" onClick={() => setConfirmDelete(false)}>
                  Cancel
                </Button>
                <Button
                  as="button"
                  buttonType="primary"
                  buttonStyle="destructive"
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </Flex>
            )}
          </>
        )}
      </CommentContent>
    </Flex>
  );
}

const CommentContent = styled(Flex)({
  minWidth: 0,
  "& .dropdown-trigger": {
    opacity: 0,
    transition: "opacity 0.2s ease",
  },

  "&:hover .dropdown-trigger": {
    opacity: 1,
  },

  "& .dropdown-trigger[data-state='open']": {
    opacity: 1,
  },
});

const Spacer = styled.div({
  marginLeft: "auto",
});

const MentionsStyled = styled(Mentions)({
  marginTop: "3px",
  marginBottom: "3px",
});
